/**
 * ProcessSteps セクション。3 variants:
 *   horizontal     — 横並び 1→2→3→4 (矢印付き)
 *   vertical       — 縦並びタイムライン (左番号 + 右説明)
 *   numbered_cards — 番号付きカードグリッド
 */
import { ArrowRight, Workflow } from 'lucide-react';

import { ArrayField, TextField, TextareaField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface Step {
  title: string;
  description: string;
}
interface ProcessStepsConfig {
  title: string;
  subtitle: string;
  steps: Step[];
}

function StepsEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as ProcessStepsConfig;
  const set = (patch: Partial<ProcessStepsConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="セクション見出し"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="サブタイトル"
        value={cfg.subtitle ?? ''}
        onChange={(v) => set({ subtitle: v })}
        onCommit={onCommit}
      />
      <ArrayField<Step>
        label="ステップ"
        items={cfg.steps ?? []}
        onChange={(next) => set({ steps: next })}
        emptyItem={() => ({ title: '', description: '' })}
        addLabel="ステップを追加"
        renderItem={(item, _i, update) => (
          <div className="space-y-2">
            <TextField
              label="ステップ名"
              value={item.title}
              onChange={(v) => update({ title: v })}
              onCommit={onCommit}
            />
            <TextareaField
              label="説明"
              value={item.description}
              onChange={(v) => update({ description: v })}
              onCommit={onCommit}
              rows={2}
            />
          </div>
        )}
      />
    </div>
  );
}

function HorizontalSteps({ config }: RendererProps) {
  const v = config as unknown as ProcessStepsConfig;
  const steps = v.steps ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-8">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="flex flex-col md:flex-row items-stretch gap-4">
        {steps.map((s, i) => (
          <div key={i} className="flex-1 flex items-start gap-3 md:flex-col md:items-center">
            <div className="size-10 rounded-full bg-[var(--accent)] text-white flex items-center justify-center font-bold flex-shrink-0">
              {i + 1}
            </div>
            <div className="flex-1 md:text-center">
              <div className="font-semibold mb-1">{s.title}</div>
              <div className="text-xs text-[var(--text-secondary)]">
                {s.description}
              </div>
            </div>
            {i < steps.length - 1 && (
              <ArrowRight className="hidden md:block size-5 text-[var(--text-muted)] absolute" />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function VerticalSteps({ config }: RendererProps) {
  const v = config as unknown as ProcessStepsConfig;
  const steps = v.steps ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-8">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="space-y-0 relative">
        {steps.map((s, i) => (
          <div key={i} className="flex gap-4 pb-6 last:pb-0">
            <div className="flex flex-col items-center">
              <div className="size-10 rounded-full bg-[var(--accent)] text-white flex items-center justify-center font-bold flex-shrink-0 z-10">
                {i + 1}
              </div>
              {i < steps.length - 1 && (
                <div className="w-px flex-1 bg-[var(--border)] mt-1 mb-1" />
              )}
            </div>
            <div className="flex-1 pt-1">
              <div className="font-semibold mb-1">{s.title}</div>
              <div className="text-sm text-[var(--text-secondary)]">
                {s.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function NumberedCardsSteps({ config }: RendererProps) {
  const v = config as unknown as ProcessStepsConfig;
  const steps = v.steps ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-8">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {steps.map((s, i) => (
          <div
            key={i}
            className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-5"
          >
            <div className="text-3xl font-bold text-[var(--accent)] mb-2 leading-none">
              {String(i + 1).padStart(2, '0')}
            </div>
            <div className="font-semibold mb-1">{s.title}</div>
            <div className="text-sm text-[var(--text-secondary)]">
              {s.description}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const sample = (): Record<string, unknown> => ({
  title: '導入の流れ',
  subtitle: 'お問い合わせから運用開始まで最短 2 週間',
  steps: [
    {
      title: 'お問い合わせ',
      description: 'フォームから 1 分でご相談いただけます。',
    },
    {
      title: 'ヒアリング',
      description: '要件・課題をお伺いし、最適なプランをご提案します。',
    },
    { title: '設定・検証', description: '初期設定とテスト運用を行います。' },
    { title: '運用開始', description: '本番稼働開始。継続的にサポートします。' },
  ] as Step[],
});

export const processStepsDefinition: SectionDefinition = {
  type: 'process_steps',
  label: 'ステップフロー',
  icon: Workflow,
  description: '導入や利用の流れを 1→2→3 で表現。',
  defaultVariant: 'horizontal',
  useCases: ['service', 'b2b_lease', 'platform'],
  variants: {
    horizontal: {
      label: '横並び',
      description: '1→2→3 を横方向に',
      Renderer: HorizontalSteps,
      Editor: StepsEditor,
      sample,
    },
    vertical: {
      label: 'タイムライン',
      description: '縦方向に時系列で',
      Renderer: VerticalSteps,
      Editor: StepsEditor,
      sample,
    },
    numbered_cards: {
      label: '番号カード',
      description: '番号付きカードのグリッド',
      Renderer: NumberedCardsSteps,
      Editor: StepsEditor,
      sample,
    },
  },
};
